import * as React from 'react';
import { Navigate, Outlet, Route, Routes as RrdRoutes, useLocation } from 'react-router';
import { ErrorBoundary } from '@studio/components/ErrorBoundary';
import { Layout } from './components/Layout';
import { PlaylistFetch } from './components/PlaylistFetch';
import { VideoPageContainer } from './components/VideoPageContainer';
import { FeedProvider } from './contexts/feedContext';
import { LivePlayerProvider } from './contexts/livePlayerContext';
import { PaginationProvider } from './contexts/paginationContext';
import { QueryProvider } from './contexts/queryContext';
import { SearchProvider } from './contexts/searchContext';
import LiveVideo from './pages/LiveVideo';
import NotFound from './pages/NotFound';

const Landing = React.lazy(() => import('./pages/Landing'));
const Search = React.lazy(() => import('./pages/Search'));
const SearchVideo = React.lazy(() => import('./pages/SearchVideo'));
const Video = React.lazy(() => import('./pages/Video'));
const VideoGrid = React.lazy(() => import('./pages/VideoGrid'));

function ErrorFallback() {
  return <Navigate to="/404" />;
}

const Routes = () => {
  const location = useLocation();

  React.useEffect(() => {
    const parsedLocation = JSON.parse(JSON.stringify(location));
    window.parent.postMessage({ data: window.btoa(parsedLocation.pathname), sender: 'resi-sites' }, '*');
  }, [location]);

  return (
    <QueryProvider>
      <FeedProvider>
        <LivePlayerProvider>
          <SearchProvider>
            <RrdRoutes>
              <Route
                element={
                  <ErrorBoundary fallback={ErrorFallback}>
                    <Outlet />
                  </ErrorBoundary>
                }
                path="/*"
              >
                <Route element={<Layout />} path=":customerSite">
                  <Route
                    element={
                      <VideoPageContainer>
                        {(videoFetchValues) => <SearchVideo {...videoFetchValues} />}
                      </VideoPageContainer>
                    }
                    path="watch/:videoId"
                  />
                  <Route
                    element={
                      <PlaylistFetch>
                        {(playlistFetchValues) => (
                          <VideoPageContainer>
                            {(videoFetchValues) => <Video {...playlistFetchValues} video={videoFetchValues} />}
                          </VideoPageContainer>
                        )}
                      </PlaylistFetch>
                    }
                    path="watch/:playlistId/:videoId"
                  />
                  <Route element={<Search />} path="search" />
                  <Route
                    element={
                      <PlaylistFetch>
                        {(playlistFetchValues) => {
                          return (
                            <PaginationProvider>
                              <VideoGrid {...playlistFetchValues} />
                            </PaginationProvider>
                          );
                        }}
                      </PlaylistFetch>
                    }
                    path="explore/:playlistId"
                  />
                  <Route element={<LiveVideo />} path="live/:webChannelId" />
                  <Route
                    element={
                      <PaginationProvider isPlaylist>
                        <Landing />
                      </PaginationProvider>
                    }
                    path="*"
                  />
                </Route>
              </Route>
              <Route element={<NotFound />} path="/404" />
              <Route element={<Navigate to="/404" />} path="/" />
              <Route element={<Navigate to="/404" />} path="*" />
            </RrdRoutes>
          </SearchProvider>
        </LivePlayerProvider>
      </FeedProvider>
    </QueryProvider>
  );
};

Routes.displayName = 'Routes';

export default Routes;
